/**
 * PageTransition Component
 *
 * Smooth page transitions using Framer Motion.
 * Provides fade and slide animations for route changes.
 *
 * Respects user preferences for reduced motion (prefers-reduced-motion: reduce).
 */

import { motion, AnimatePresence } from 'framer-motion'
import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

interface PageTransitionProps {
  children: ReactNode
  /** Transition variant: fade, slide, or scale */
  variant?: 'fade' | 'slide' | 'scale'
  /** Duration in seconds */
  duration?: number
}

// Animation variants
const variants = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slide: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  },
  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
}

export function PageTransition({
  children,
  variant = 'fade',
  duration = 0.3,
}: PageTransitionProps) {
  const location = useLocation()

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={variants[variant].initial}
        animate={variants[variant].animate}
        exit={variants[variant].exit}
        transition={{
          duration,
          ease: 'easeInOut',
        }}
        style={{
          width: '100%',
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

PageTransition.displayName = 'PageTransition'
